import React from "react";
import { FormattedMessage, IntlShape } from "react-intl";
import { CampaignMessage } from "./mockData";

export function getCampaignSortTimestamp(message: CampaignMessage): number {
  const raw =
    message.status === "sent"
      ? message.sentAt
      : message.status === "scheduled" && message.scheduledAt
      ? message.scheduledAt
      : message.updatedAt || message.date;

  if (!raw) {
    return 0;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function formatCampaignListDate(
  intl: IntlShape,
  message: CampaignMessage
): string {
  const raw =
    message.status === "sent"
      ? message.sentAt
      : message.status === "scheduled" && message.scheduledAt
      ? message.scheduledAt
      : message.updatedAt || message.date;

  if (!raw) {
    return "—";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return String(raw);
  }

  return intl.formatDate(parsed, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function hasPreviewHtml(html?: string) {
  if (!html) {
    return false;
  }
  const stripped = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .trim();
  return stripped.length > 0 || html.includes("<img");
}

export function EmailPreviewThumb({
  html,
  accent,
  subject,
}: {
  html?: string;
  accent: string;
  subject: string;
}) {
  if (hasPreviewHtml(html)) {
    return (
      <div
        className="email-mkt__message-thumb email-mkt__message-thumb--preview"
        aria-hidden="true"
      >
        <div
          className="email-mkt__message-thumb-scaler"
          dangerouslySetInnerHTML={{ __html: html || "" }}
        />
      </div>
    );
  }

  return (
    <div
      className="email-mkt__message-thumb email-mkt__message-thumb--fallback"
      style={{ borderColor: accent }}
      aria-hidden="true"
    >
      <span style={{ background: accent }} title={subject} />
    </div>
  );
}

export function CampaignMetric({
  isSent,
  value,
  count,
  intl,
}: {
  isSent: boolean;
  value: string;
  count: number;
  intl: IntlShape;
}) {
  if (!isSent) {
    return <span className="email-mkt__metric email-mkt__metric--empty">—</span>;
  }

  return (
    <span className="email-mkt__metric">
      <strong>{value}</strong>
      <small>{intl.formatNumber(count)}</small>
    </span>
  );
}

export function CampaignStatusPill({
  status,
}: {
  status: CampaignMessage["status"];
}) {
  const labelId =
    status === "draft"
      ? "app.emailMarketing.emails.status.draft"
      : status === "scheduled"
      ? "app.emailMarketing.emails.status.scheduled"
      : "app.emailMarketing.emails.status.sent";

  return (
    <span className={`email-mkt__status-pill email-mkt__status-pill--${status}`}>
      <FormattedMessage id={labelId} />
    </span>
  );
}

export function CampaignAudienceLine({
  message,
  intl,
  fallbackCount = 0,
}: {
  message: CampaignMessage;
  intl: IntlShape;
  fallbackCount?: number;
}) {
  const audience = message.audience || "Wembii";
  const recipientCount = message.sentTo > 0 ? message.sentTo : fallbackCount;
  const count = intl.formatNumber(recipientCount);

  if (message.status === "sent") {
    return (
      <FormattedMessage
        id="app.emailMarketing.emails.sentTo"
        values={{ count, audience }}
      />
    );
  }

  if (message.status === "scheduled") {
    return (
      <FormattedMessage
        id="app.emailMarketing.emails.scheduledAudience"
        values={{ count, audience }}
      />
    );
  }

  return (
    <FormattedMessage
      id="app.emailMarketing.emails.draftAudience"
      values={{ audience }}
    />
  );
}
